import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Bot, Plus, PlugZap, Search, Server, Sparkles, Unplug, Workflow } from "lucide-react";
import { slugFromLabel } from "@ncp/forms";
import { formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, Input, NativeSelect, Skeleton, Tooltip, cn, notifySaved } from "@ncp/ui";
import { EMPTY_CONNECTOR, useConnectorActions, useConnectorOptions, useConnectors, type ConnectorAuthKind, type ConnectorKind, type ConnectorRow, type ConnectorUsage } from "./api";
import { validBaseUrl } from "./lint";

const CONNECTOR_KEY = /^[a-z][a-z0-9-]{1,47}$/;
const BUILDABLE: ConnectorKind[] = ["Rest", "Soap", "InboundWebhook"];
const USAGE_ICON: Record<string, typeof Bot> = { agent: Bot, assistant: Sparkles, automation: Workflow };

/** Connector Builder list: every connector with its versions, health over the last day and who uses it. */
export function ConnectorsPanel({ creating, onCreatingChange }: { creating: boolean; onCreatingChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const connectors = useConnectors();
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (connectors.data ?? []).filter((r) => !term || [r.key, r.name, r.nameAr, r.owner, r.baseUrl, ...r.tags].some((x) => x?.toLowerCase().includes(term)));
  }, [connectors.data, q]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin:connectors.search")} className="ps-9" />
        </div>
        <p className="text-xs text-muted-foreground">{t("admin:connectors.count", { count: rows.length })}</p>
      </div>
      {connectors.isLoading ? <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-48" />)}</div> : rows.length === 0 ? (
        <EmptyState icon={PlugZap} title={t("admin:connectors.emptyTitle")} description={t("admin:connectors.emptyBody")} action={<Button variant="gold" onClick={() => onCreatingChange(true)}><Plus /> {t("admin:connectors.new")}</Button>} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((r) => <ConnectorCard key={r.key} row={r} locale={locale} />)}
        </div>
      )}
      <CreateConnectorDialog open={creating} onOpenChange={onCreatingChange} existing={connectors.data ?? []} onCreated={(key) => navigate(`/integrations/connectors/${encodeURIComponent(key)}`)} />
    </div>
  );
}

function ConnectorCard({ row, locale }: { row: ConnectorRow; locale: string }) {
  const { t } = useTranslation();
  const d = row.definition;
  const health = row.circuitOpen ? "danger" : row.health === "Healthy" ? "success" : row.health === "Degraded" ? "warning" : row.health === "Down" ? "danger" : "outline";
  const byKind = groupUsage(row.usage);
  return (
    <Card className={cn("flex flex-col p-4 transition hover:border-aegold-300", row.circuitOpen && "border-danger-100")}>
      <div className="flex items-start justify-between gap-2">
        <Link to={`/integrations/connectors/${encodeURIComponent(row.key)}`} className="min-w-0">
          <p className="truncate text-sm font-semibold hover:text-aegold-800">{locale === "ar" && row.nameAr ? row.nameAr : row.name}</p>
          <p className="truncate font-mono text-[11px] text-muted-foreground" dir="ltr">{row.key} · {row.baseUrl}</p>
        </Link>
        <Badge variant={health} dot pulse={row.circuitOpen}>{row.circuitOpen ? <><Unplug /> {t("admin:integrations.circuitOpen")}</> : t(`admin:integrations.healths.${row.health}`, { defaultValue: row.health })}</Badge>
      </div>
      {row.description ? <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{row.description}</p> : null}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Badge variant="outline" size="sm">{t(`admin:connectors.kind.${row.kind}`, { defaultValue: row.kind })}</Badge>
        <Badge variant="outline" size="sm">{t(`admin:connectors.auth.kind.${row.authKind}`, { defaultValue: row.authKind })}</Badge>
        {d.publishedVersion ? <Badge variant="success" size="sm">v{d.publishedVersion} {t("admin:forms.status.Published")}</Badge> : null}
        {d.draftVersion ? <Badge variant={d.draftStatus === "InReview" ? "warning" : "info"} size="sm">v{d.draftVersion} {t(`admin:forms.status.${d.draftStatus ?? "Draft"}`)}</Badge> : null}
        {!row.enabled ? <Badge variant="default" size="sm">{t("admin:common.disabled")}</Badge> : null}
        {row.owner ? <Badge variant="outline" size="sm"><Server className="size-3" /> {row.owner}</Badge> : null}
      </div>
      <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
        <div><p className="text-muted-foreground">{t("admin:connectors.operations")}</p><p className="tabular font-semibold">{row.operations}</p></div>
        <div><p className="text-muted-foreground">{t("admin:integrations.calls")}</p><p className="tabular font-semibold">{row.calls24h}</p></div>
        <div><p className="text-muted-foreground">{t("admin:integrations.errors")}</p><p className={cn("tabular font-semibold", row.errors24h > 0 && "text-danger-600")}>{row.errors24h}</p></div>
        <div><p className="text-muted-foreground">p95</p><p className="tabular font-semibold">{row.p95 != null ? `${row.p95} ms` : "—"}</p></div>
      </div>
      <div className="mt-auto flex flex-wrap items-center gap-1.5 border-t border-sand-100 pt-3 text-[11px]">
        {Object.entries(byKind).map(([kind, items]) => {
          const Icon = USAGE_ICON[kind] ?? Workflow;
          return <Tooltip key={kind} content={items.map((u) => `${u.name} (${u.operations.join(", ")})`).join(" · ")}><Badge variant="ai" size="sm"><Icon className="size-3" /> {t(`admin:connectors.usage.${kind}`, { count: items.length, defaultValue: kind })}</Badge></Tooltip>;
        })}
        {row.usage.length === 0 ? <span className="text-muted-foreground">{t("admin:connectors.unused")}</span> : null}
        <span className="ms-auto text-muted-foreground">{row.lastCallAt ? formatRelative(row.lastCallAt, locale as "en" | "ar") : t("admin:integrations.noCalls")}</span>
      </div>
    </Card>
  );
}

function groupUsage(usage: ConnectorUsage[]): Record<string, ConnectorUsage[]> {
  const out: Record<string, ConnectorUsage[]> = {};
  for (const u of usage) (out[u.kind] ??= []).push(u);
  return out;
}

function CreateConnectorDialog({ open, onOpenChange, existing, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; existing: ConnectorRow[]; onCreated: (key: string) => void }) {
  const { t } = useTranslation();
  const actions = useConnectorActions();
  const options = useConnectorOptions();
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [kind, setKind] = useState<ConnectorKind>("Rest");
  const [baseUrl, setBaseUrl] = useState("https://");
  const [authKind, setAuthKind] = useState<ConnectorAuthKind>("None");
  const [owner, setOwner] = useState("");
  const effectiveKey = keyTouched ? key : slugFromLabel(name).replace(/[._]/g, "-").slice(0, 48);
  const keyError = !effectiveKey ? null : !CONNECTOR_KEY.test(effectiveKey) ? t("admin:connectors.create.keyInvalid") : existing.some((c) => c.key === effectiveKey) ? t("admin:agents.create.keyTaken") : null;
  const urlError = kind !== "InboundWebhook" && baseUrl !== "https://" && !validBaseUrl(baseUrl) ? t("admin:connectors.lint.baseUrl") : null;
  const ready = name.trim().length > 1 && !!effectiveKey && !keyError && (kind === "InboundWebhook" || validBaseUrl(baseUrl));
  const reset = () => { setName(""); setNameAr(""); setKey(""); setKeyTouched(false); setKind("Rest"); setBaseUrl("https://"); setAuthKind("None"); setOwner(""); };

  const create = async () => {
    try {
      const d = await actions.create.mutateAsync({
        key: effectiveKey,
        body: {
          ...EMPTY_CONNECTOR, name: name.trim(), nameAr: nameAr.trim() || null, kind, authKind, owner: owner.trim() || null,
          baseUrl: kind === "InboundWebhook" ? "" : baseUrl.trim(), inboundPath: kind === "InboundWebhook" ? effectiveKey : null,
        },
      });
      notifySaved(d, t("admin:connectors.create.created"));
      onOpenChange(false); reset();
      onCreated(d.key);
    } catch { /* useSave reported the failure with its cause; the dialog stays open */ }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent size="md">
        <DialogHeader><DialogTitle>{t("admin:connectors.new")}</DialogTitle><DialogDescription>{t("admin:connectors.create.intro")}</DialogDescription></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("admin:common.name")} required><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("admin:connectors.create.namePlaceholder")} autoFocus /></Field>
          <Field label={t("admin:common.nameAr")}><Input value={nameAr} dir="rtl" onChange={(e) => setNameAr(e.target.value)} /></Field>
          <Field label={t("admin:common.key")} hint={t("admin:connectors.create.keyHint")} error={keyError ?? undefined}><Input value={effectiveKey} dir="ltr" className="font-mono" onChange={(e) => { setKeyTouched(true); setKey(e.target.value.toLowerCase()); }} /></Field>
          <Field label={t("admin:connectors.fields.owner")} hint={t("admin:connectors.fields.ownerHint")}><Input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="FCSC" /></Field>
          <Field label={t("admin:connectors.fields.kind")}>
            <NativeSelect value={kind} onChange={(e) => setKind(e.target.value as ConnectorKind)}>
              {BUILDABLE.map((k) => <option key={k} value={k}>{t(`admin:connectors.kind.${k}`)}</option>)}
            </NativeSelect>
          </Field>
          <Field label={t("admin:connectors.auth.kindLabel")}>
            <NativeSelect value={authKind} onChange={(e) => setAuthKind(e.target.value as ConnectorAuthKind)} disabled={kind === "InboundWebhook"}>
              {(options.data?.authKinds ?? [{ kind: "None" as const }]).map((a) => <option key={a.kind} value={a.kind}>{t(`admin:connectors.auth.kind.${a.kind}`, { defaultValue: a.kind })}</option>)}
            </NativeSelect>
          </Field>
          {kind !== "InboundWebhook" ? (
            <Field label={t("admin:connectors.fields.baseUrl")} hint={t("admin:connectors.fields.baseUrlHint", { url: options.data?.simulatorsBaseUrl ?? "" })} error={urlError ?? undefined} className="sm:col-span-2">
              <Input value={baseUrl} dir="ltr" className="font-mono text-xs" onChange={(e) => setBaseUrl(e.target.value)} />
            </Field>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("admin:common.cancel")}</Button>
          <Button variant="gold" disabled={!ready} loading={actions.create.isPending} onClick={create}><Plus /> {t("admin:connectors.create.createOpen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
